import { useTranslation } from 'react-i18next'
import type { CamionMuelle, CargaSalida, CuadroMuelles, SlotMuelle } from '@/types'
import type { DockTrack } from '@/utils/dockTimeTracks'
import { GanttTrackHeader } from '@/components/planning/GanttTrackHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { INFO_COL_DOCK } from '@/config/dockSchedule'
import { DockRow } from '@/components/muelles/DockRow'
import { FranjaOcupacion } from '@/components/muelles/FranjaOcupacion'

/**
 * La rejilla del cuadro: cabecera del eje + una fila por muelle.
 *
 * Reutiliza `GanttTrackHeader`, que ya sabe repartir columnas por su peso en
 * `fr` — le da igual que sean días de una semana o las horas de una jornada.
 */
export function MuellesTimeline({
  cuadro, track, cargasPorNumero, dark, onCamion, onHueco, onSoltarCarga,
}: {
  cuadro: CuadroMuelles
  track: DockTrack
  cargasPorNumero: Map<string, CargaSalida>
  dark: boolean
  onCamion?: (c: CamionMuelle) => void
  onHueco?: (slot: SlotMuelle, cuando: Date) => void
  onSoltarCarga?: (carga: string, destino: { camion?: CamionMuelle; slot: SlotMuelle; cuando: Date }) => void
}) {
  const { t } = useTranslation()

  const porMuelle = new Map<string, CamionMuelle[]>()
  for (const c of cuadro.camiones) {
    const clave = (c.muelle ?? '').trim()
    const lista = porMuelle.get(clave)
    if (lista) lista.push(c)
    else porMuelle.set(clave, [c])
  }

  if (cuadro.muelles.length === 0) {
    // No es un error ni una pantalla en blanco: es que el catálogo de slots está
    // sin rellenar. Hay que decirlo, no aparentar que el almacén no tiene muelles.
    return (
      <EmptyState
        dark={dark}
        title={t('muelles.sinCatalogo.titulo')}
        description={t('muelles.sinCatalogo.detalle')}
      />
    )
  }

  return (
    <div className={`rounded-lg border overflow-hidden ${dark ? 'border-white/10' : 'border-slate-200'}`}>
      <GanttTrackHeader
        track={track}
        dark={dark}
        titulo={t('muelles.header')}
        anchoInfo={INFO_COL_DOCK}
        conMarcas={false}
      />
      <FranjaOcupacion
        camiones={cuadro.camiones}
        muelles={cuadro.muelles}
        track={track}
        dark={dark}
      />
      {cuadro.muelles.map((slot) => (
        <DockRow
          key={slot.nombre}
          slot={slot}
          camiones={porMuelle.get(slot.nombre) ?? []}
          cargasPorNumero={cargasPorNumero}
          track={track}
          dark={dark}
          onCamion={onCamion}
          onHueco={onHueco}
          onSoltarCarga={onSoltarCarga}
        />
      ))}
    </div>
  )
}
